"use client"

import { useEffect, useRef, useCallback } from "react"
import { usePathname, useRouter } from "next/navigation"
import { getCookie } from "@/utils/cookieUtils"
import { nativeBr } from "@/utils/nativeBr"
import useTokenAuthentication from "@/hooks/member/useTokenAuthentication"
import { useRenderingRefresh } from "@/hooks/common/useRenderingRefresh"
import { usePopupStore } from "@/components/common/popup/store/usePopupStore"
import SessionExpirePopup from "./SessionExpirePopup"

interface AuthGuardProps {
  children: React.ReactNode
}

const AuthGuard = ({ children }: AuthGuardProps) => {
  const pathname = usePathname()
  const router = useRouter()
  const { handleRefreshTokens, handleLogout } = useTokenAuthentication({})
  const containerRef = useRef<HTMLDivElement>(null)
  const { openPopup, closePopup } = usePopupStore()

  const { checkForRenderingFailure, clearRefreshFlag } = useRenderingRefresh({
    enableLogging: true,
  })

  const handleRenderingCheck = useCallback(() => {
    if (!containerRef.current) return
    checkForRenderingFailure(containerRef.current)
  }, [checkForRenderingFailure])

  // 토큰 검증 실패 시 세션 만료 팝업 표시
  const handleTokenExpired = useCallback(async () => {
    if (nativeBr.isEnabled) {
      // 네이티브 앱 환경: 세션 만료 팝업 표시
      openPopup(
        <SessionExpirePopup
          onConfirm={async () => {
            closePopup()
            await handleLogout()
          }}
        />,
      )
    } else {
      await handleLogout()
      router.push("/403")
    }
    // TODO: 성능테스트 이후 다시 검증
    // else {
    //   // 웹 환경: 즉시 로그아웃 처리
    //   handleLogout()
    // }
  }, [router, openPopup, closePopup, handleLogout])

  // 토큰 리프레시 시도 함수
  const checkAndRefreshToken = useCallback(async () => {
    const hasToken = getCookie("rlHasToken")

    if (!hasToken) {
      await handleTokenExpired()
      return false
    }

    // rlHasToken 값은 ISO 문자열 타임스탬프
    // URL 인코딩된 값을 디코딩 (예: %3A → :)
    const decodedToken = decodeURIComponent(hasToken)

    // 3분(180000ms)이 지났는지 확인
    const tokenTimestamp = new Date(decodedToken).getTime()
    const now = new Date().getTime()
    const threeMinutesInMs = 3 * 60 * 1000 // 3분 = 180000ms

    // Invalid Date 체크
    const force = isNaN(tokenTimestamp) ? true : now - tokenTimestamp >= threeMinutesInMs

    const result = await handleRefreshTokens(force)
    if (result) {
      // iOS 환경일 때 세션 연장 네이티브 콜백
      if (nativeBr.isIOS) {
        nativeBr.DGTsessionExtend()
      }
      return true
    } else {
      // 토큰 리프레시 실패
      await handleTokenExpired()
      return false
    }
  }, [handleRefreshTokens, handleTokenExpired])

  // 라우터 변경 시 토큰 검증
  useEffect(() => {
    // 새로운 렌더링 시작 시 리프레시 플래그 클리어
    clearRefreshFlag()

    if (
      pathname.indexOf("/intro") !== -1 ||
      pathname.indexOf("/403") !== -1
    ) {
      // /intro, /403 페이지는 인증 체크에서 제외
      return
    }

    // 토큰 리프레시 후 DOM 내용 확인
    checkAndRefreshToken().then((success) => {
      if (success && pathname !== "/search") {
        handleRenderingCheck()
      }
    })
  }, [pathname, clearRefreshFlag, checkAndRefreshToken, handleRenderingCheck])

  // visibilitychange 이벤트로 사용자 복귀 시 토큰 검증
  useEffect(() => {
    const handleVisibilityChange = () => {
      // 사용자가 다시 페이지로 복귀했을 때만 검증
      if (document.visibilityState === "visible") {
        if (
          pathname.indexOf("/intro") !== -1 ||
          pathname.indexOf("/403") !== -1
        ) {
          return
        }

        // 토큰 검증 시도
        checkAndRefreshToken()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [pathname, checkAndRefreshToken])

  return (
    <div ref={containerRef} className="flex h-full w-full flex-1 flex-col">
      {children}
    </div>
  )
}

export default AuthGuard
